/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: SystemMiscPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main system miscellaneous settings page with configuration management
    and system monitoring capabilities.

 ?? Notes:
    - Integrates SystemConfigFormView for configuration management
    - Supports system-wide settings and monitoring
    - Implements comprehensive system management functionality

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Search } from 'lucide-react';

import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useSystemMiscList } from './hooks/useSystemMiscList';
import { useQuickList } from './hooks/useQuickList';
import { SystemMiscActions } from './components/SystemMiscActions';
import { QuickListModal } from './components/QuickListModal';
import { refreshServerStatus } from './api';
import { SystemMiscConfig } from './types';

function SystemMiscPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showError, showSuccess } = useNotifications();

  const tenantSlug = user?.tenant?.slug ?? t('systemMisc.defaultTenant');
  const userId = user?.id ? Number(user.id) : undefined;

  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const {
    configs,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useSystemMiscList();

  const quickList = useQuickList();

  const handleViewDetails = useCallback(
    (config: SystemMiscConfig) => {
      const target = `/admin/system-settings/system-misc/${config.uuid ?? config.id}`;
      navigate(target);
    },
    [navigate],
  );

  const handleRefreshStatus = useCallback(
    async (config: SystemMiscConfig) => {
      const targetServerId = config.serverId ?? config.server?.id;
      if (!targetServerId) {
        showError(t('systemMisc.serverNotFound'));
        return;
      }

      try {
        setRefreshingId(String(config.id));
        const result = await refreshServerStatus(targetServerId, tenantSlug, userId);
        showSuccess(
          t('systemMisc.serverStatusRefreshed', {
            name: config.name,
            status: result?.data?.serverStatus ?? 'unknown',
          }),
        );
        refresh();
      } catch (error) {
        showError(error instanceof Error ? error.message : t('systemMisc.failedToRefreshStatus'));
      } finally {
        setRefreshingId(null);
      }
    },
    [refresh, showError, showSuccess, t, tenantSlug, userId],
  );

  const renderActions = useCallback(
    (config: SystemMiscConfig) => (
      <SystemMiscActions
        config={config}
        refreshingId={refreshingId}
        onEdit={handleViewDetails}
        onRefresh={handleRefreshStatus}
      />
    ),
    [handleRefreshStatus, handleViewDetails, refreshingId],
  );

  const handleQuickListSelect = useCallback(
    (config: SystemMiscConfig) => {
      quickList.handleSelect(config, handleViewDetails);
    },
    [handleViewDetails, quickList],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('systemMisc.systemStatus')}
          titleIcon={<Settings className="h-5 w-5 text-primary" />}
          data={configs}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          addButtonText={undefined}
          emptyMessage={t('systemMisc.noServersFound')}
          searchPlaceholder={t('systemMisc.searchServers')}
          renderActions={renderActions}
          actions={
            <button
              type="button"
              onClick={() => quickList.setShowQuickList(true)}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <Search className="h-4 w-4" />
              {t('systemMisc.quickList')}
            </button>
          }
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    >
      <QuickListModal
        show={quickList.showQuickList}
        search={quickList.quickListSearch}
        configs={quickList.quickListConfigs}
        isLoading={quickList.isLoading}
        onSearchChange={quickList.setQuickListSearch}
        onSelect={handleQuickListSelect}
        onClose={quickList.handleClose}
      />
    </ModulePageContainer>
  );
}

export default SystemMiscPage;
